import { BlockLoading } from '../fetch-status/block-loading';
import { Input } from '../ui/input';
import { useBalance } from './hooks/use-balance';
import { useTradeSettings } from './hooks/use-trade-settings';
import { MarketResult } from './market-results';
import './style.css';
import { TradeConfirm } from './trade-confirm';
import { TreadInput } from './trade-input';
import { TreadOptions } from './trade-options';
import { TradeSettings } from './trade-settings';
import { shouldRefetchTradeQuery } from './trade-success-queries';
import { TreadSwitch } from './trade-switch';
import { CLOB_ENABLED, TERMS_OF_USE_LINK } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useAppSettingStore } from '@/client/store/app-store';
import { TradeParams, useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { MarketStatus } from '@/libs/sdk/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUnits, parseUnits } from 'viem';

type TradePanelProps = {
  className?: string;
  tradeClassName?: string;
  params?: TradeParams;
  beforeOptions?: React.ReactNode;
  afterOptions?: React.ReactNode;
};

export const TradePanel = ({ className, tradeClassName, beforeOptions, afterOptions }: TradePanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isAuthed } = useAuth();
  const uid = useMemo(() => data?.uid ?? '', [data?.uid]);
  const [successMessage, setSuccessMessage] = useState<string>();
  const {
    marketAddress,
    optionAddress,
    setOptionAddress,
    optionSide,
    setOptionSide,
    type,
    setType,
    amount,
    setAmount,
    tradeMode,
    setTradeMode,
    limitPrice,
    setLimitPrice,
  } = useTradeParamsStore(state => state);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const market = useMarketDetail(api, { baseTokenAddress, marketAddress });
  const isExpire = useMemo(() => Number(market.data?.deadline) <= Math.ceil(Date.now() / 1e3), [market.data?.deadline]);
  const balance = useBalance({ type, marketAddress, optionAddress, uid });
  const { minAmount, deadlineTime } = useTradeSettings(amount);
  // For limit orders, price is a USD decimal string in [$0.01, $0.99]. Empty = not yet set.
  const limitPriceNum = useMemo(() => {
    if (!limitPrice) return NaN;
    const n = parseFloat(limitPrice);
    return Number.isFinite(n) ? n : NaN;
  }, [limitPrice]);
  const limitPriceValid = useMemo(
    () => Number.isFinite(limitPriceNum) && limitPriceNum >= 0.01 && limitPriceNum <= 0.99,
    [limitPriceNum]
  );
  const disabled = useMemo(() => {
    if (isExpire) return true;
    if (CLOB_ENABLED && tradeMode === 'limit' && !limitPriceValid) return true;
    // Unauth users: skip balance check, AuthedButton will trigger Sign In.
    if (!isAuthed) return false;
    return !balance.data || Number(amount) > balance.data || balance.isLoading;
  }, [amount, balance.data, balance.isLoading, isExpire, tradeMode, limitPriceValid, isAuthed]);

  useEffect(() => {
    setSuccessMessage(undefined);
  }, [amount, marketAddress, optionAddress, type]);

  const handleTradeSuccess = async () => {
    await queryClient.refetchQueries({
      predicate: query =>
        shouldRefetchTradeQuery(query.queryKey, {
          uid,
          baseTokenAddress,
          marketAddress,
        }),
    });
    setSuccessMessage(type === 'buy' ? t('buy-success') : t('sell-success'));
  };

  if (market.isLoading || !market.data) return <BlockLoading />;
  if (market.data.marketStatus >= MarketStatus.settlement)
    return <MarketResult marketAddress={market.data.marketAddress} uid={uid} />;
  return (
    <div className={cn('w-full space-y-3', className)}>
      <div
        className={cn(
          'bg-card w-full space-y-4 md:rounded-6',
          'md:border-.5 md:border-foreground/20 md:p-4 md:space-y-6 md:shadow-[0px_7px_15px_0px] md:shadow-card/10',
          'transition-all duration-100 ease-in-out bounceHeight',
          type === 'buy' && 'min-h-[374px]',
          type === 'sell' && 'min-h-[382px]',
          tradeClassName
        )}
      >
        <TreadSwitch
          type={type}
          setType={type => {
            if (amount) {
              const input = formatUnits(amount, balance.decimal);
              setAmount(parseUnits(input, (type == 'buy' ? balance.buyDecimal : balance.sellDecimal) ?? 6));
            }
            setType(type);
          }}
          tradeMode={CLOB_ENABLED ? tradeMode : 'market'}
          setTradeMode={CLOB_ENABLED ? setTradeMode : undefined}
        />
        {beforeOptions}
        <TreadOptions
          type={type}
          market={market.data}
          optionAddress={optionAddress}
          setOptionAddress={setOptionAddress}
          optionSide={optionSide}
          setOptionSide={setOptionSide}
          uid={uid}
        />
        {afterOptions}
        {CLOB_ENABLED && tradeMode === 'limit' && (
          <div className="space-y-1">
            <div className="text-foreground/70 flex items-center justify-between">
              <span className="text-sm leading-3">{t('limit-price', 'Limit price')}</span>
            </div>
            <div className="pos-relative mt-2">
              <span className="pos-absolute start-3 top-1/2 -translate-y-1/2 text-foreground/70 text-base">$</span>
              <Input
                type="text"
                inputMode="decimal"
                placeholder={t('limit-price-placeholder', 'Enter price ($0.01 - $0.99)')}
                value={limitPrice}
                onChange={e => {
                  const v = e.target.value;
                  // Accept empty, "0", ".", ".4", "0.", "0.4", "0.41" — Polymarket-style USD decimals.
                  if (v === '' || /^0?\.\d{0,2}$|^0$|^\.\d{0,2}$/.test(v)) setLimitPrice(v);
                }}
                className="border border-foreground/10 focus:border-foreground/40 py-4 h-14 !text-6 leading-8 placeholder:leading-8 placeholder:text-4 ps-8 shadow-none"
              />
            </div>
            {!!limitPrice && !limitPriceValid && (
              <div className="text-xs text-destructive">
                {t('limit-price-invalid', 'Price must be between $0.01 and $0.99')}
              </div>
            )}
          </div>
        )}
        <div className="space-y-1">
          <div className="text-foreground/70 flex items-center justify-between">
            <span className="text-sm leading-3">{type == 'sell' ? t('shares') : t('amount')}</span>
            <TradeSettings />
          </div>
          <TreadInput
            disabled={isExpire}
            type={type}
            maxAmount={balance.data}
            value={amount}
            onValueChange={setAmount}
            isLoading={balance.isLoading}
            decimal={balance.decimal}
            showBalance={isAuthed}
          />
        </div>
        {isExpire && (
          <div className="text-center text-sm text-destructive mb-4">{t('market-closed-awaiting-resolution')}</div>
        )}
        {!isExpire && !optionAddress && amount != undefined && (
          <div className="text-center text-sm text-destructive mb-4">{t('please-choose-a-outcome')}</div>
        )}
        {successMessage && (
          <div className="rounded-3 border border-beneficial/30 bg-beneficial/10 px-4 py-3 text-sm font-medium text-beneficial">
            {successMessage}
          </div>
        )}
        <TradeConfirm
          type={type}
          marketAddress={marketAddress}
          optionAddress={optionAddress}
          optionSide={optionSide}
          amount={amount}
          minAmount={minAmount}
          deadline={deadlineTime}
          disabled={disabled}
          uid={uid}
          onSuccess={handleTradeSuccess}
        />
      </div>
      {/* copyright */}
      <p className="text-xs font-medium leading-3 text-center text-foreground/70">
        {t('by-trading-you-agree-to-the')}
        <a href={TERMS_OF_USE_LINK} target="_blank" className="underline ms-1">
          {t('terms-of-use')}
        </a>
      </p>
    </div>
  );
};
